import { Priority } from "./types";

export function add(x: Priority, y: Priority): Priority {
  if (x == null || y == null) return undefined;
  return x + y;
}

export function sub(x: Priority, y: Priority): Priority {
  if (x == null || y == null) return undefined;
  return x - y;
}

export function div(x: Priority, y: Priority): Priority {
  if (x == null || y == null || !y) return undefined;
  return x / y;
}

export function avg(x: Priority, y: Priority): Priority {
  if (x == null || y == null) return undefined;
  return (x + y) / 2;
}

export function cmp(x: Priority, y: Priority): -1 | 0 | 1 | undefined {
  if (x == null || y == null) return undefined;
  return x < y ? -1 : x > y ? +1 : 0;
}

function getAverageDistance(p: Priority[]) {
  const distances = p
    .map((_, i) => sub(p[i], p[i + 1]))
    .filter((d): d is number => d != null);
  const total = distances.reduce((x, y) => x + y, 0);
  const count = distances.length;
  return div(total, count);
}

export function getPriorityIfInsertBefore(p: Priority[], i: number) {
  if (i === 0) {
    return add(p[i], getAverageDistance(p));
  } else {
    return avg(p[i], p[i - 1]);
  }
}

export function getPriorityIfInsertAfter(p: Priority[], i: number) {
  if (i === p.length - 1) {
    return sub(p[i], getAverageDistance(p));
  } else {
    return avg(p[i], p[i + 1]);
  }
}

export function getPriorityIfBringToFront(p: Priority[], i: number) {
  if (i === 0) {
    return undefined;
  } else {
    return add(p[0], getAverageDistance(p));
  }
}

export function getPriorityIfSendToBack(p: Priority[], i: number) {
  if (i === p.length - 1) {
    return undefined;
  } else {
    return sub(p[p.length - 1], getAverageDistance(p));
  }
}

export function getPriorityIfBringForward(p: Priority[], i: number) {
  return getPriorityIfInsertBefore(p, i - 1);
}

export function getPriorityIfSendBackward(p: Priority[], i: number) {
  return getPriorityIfInsertAfter(p, i + 1);
}

export function getPriorityIfMoveXToY(
  p: Priority[],
  x: number | null | undefined,
  y: number | null | undefined
) {
  if (x == null || y == null) return undefined;
  switch (cmp(p[x], p[y])) {
    case -1:
      return getPriorityIfInsertBefore(p, y); // ... Y ... X ...
    case +1:
      return getPriorityIfInsertAfter(p, y); // ... X ... Y ...
    default:
      return undefined;
  }
}

export function getPriorityList<T>({
  items,
  exhausted,
  toPriority,
}: {
  items: T[];
  exhausted: boolean;
  toPriority: (item: T) => Priority;
}) {
  // if the list is not exhausted, we add undefined at the end so that actions
  // like Send to Back and Send Backward (for the last item) are disabled
  return [...items.map(toPriority), ...(exhausted ? [] : [undefined])];
}
